import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Beaker, Thermometer, Clock, Droplet } from "lucide-react";

export default function ExtractionScience() {
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/data/extraction_science.md')
      .then(res => res.text())
      .then(text => {
        setContent(text);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F4F2EC] via-white to-[#F4F2EC]">
        <div className="container py-12">
          <Skeleton className="h-12 w-96 mb-8" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  const species = [
    {
      name: "Cordyceps militaris",
      icon: "🟥",
      key: "cordyceps",
      color: "from-red-500 to-orange-600",
      compounds: ["Cordycepin", "Adenosine", "Beta-glucans"],
      optimalTemp: "60-65°C",
      optimalSolvent: "50-60% aqueous ethanol"
    },
    {
      name: "Lion's Mane",
      icon: "🦁",
      key: "lions-mane",
      color: "from-amber-500 to-[#C8A53D]",
      compounds: ["Erinacines", "Hericenones", "Beta-glucans"],
      optimalTemp: "40-65°C",
      optimalSolvent: "30-70% aqueous ethanol"
    },
    {
      name: "Reishi",
      icon: "🍄",
      key: "reishi",
      color: "from-[#F4F2EC]0 to-[#C26A1E]",
      compounds: ["Triterpenes", "Polysaccharides", "Ganoderic acids"],
      optimalTemp: "60-80°C",
      optimalSolvent: "Dual extraction"
    },
    {
      name: "Chaga",
      icon: "⚫",
      key: "chaga",
      color: "from-gray-700 to-gray-900",
      compounds: ["Betulinic acid", "Polysaccharides", "Melanin"],
      optimalTemp: "60-80°C",
      optimalSolvent: "Hot water + ethanol"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F2EC] via-white to-[#F4F2EC]">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link href="/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Extraction Science</h1>
                <p className="text-gray-600 mt-1">Optimal methods for bioactive compound preservation</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-12">
        {/* Key Findings */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center mb-3">
                <Thermometer className="w-6 h-6 text-orange-600" />
              </div>
              <CardTitle className="text-lg">Temperature Control</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-orange-600 mb-2">60-80°C</p>
              <p className="text-sm text-gray-600">Optimal range for most bioactives vs &gt;100°C degradation</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-[#F4F2EC] flex items-center justify-center mb-3">
                <Droplet className="w-6 h-6 text-[#A98A2C]" />
              </div>
              <CardTitle className="text-lg">Multi-Solvent</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-[#A98A2C] mb-2">Superior</p>
              <p className="text-sm text-gray-600">Dual extraction captures full spectrum vs single-solvent</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-[#F4F2EC] flex items-center justify-center mb-3">
                <Beaker className="w-6 h-6 text-[#B45F3D]" />
              </div>
              <CardTitle className="text-lg">Ultrasonic</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-[#B45F3D] mb-2">+20-50%</p>
              <p className="text-sm text-gray-600">Yield improvement with proper frequency/duration</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-[#F4F2EC] flex items-center justify-center mb-3">
                <Clock className="w-6 h-6 text-[#A98A2C]" />
              </div>
              <CardTitle className="text-lg">Non-Alcoholic</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-[#A98A2C] mb-2">Better</p>
              <p className="text-sm text-gray-600">Preservation than weak alcohol solutions</p>
            </CardContent>
          </Card>
        </div>

        {/* Species Cards */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Species-Specific Protocols</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {species.map((sp) => (
              <Card key={sp.key} className="border-0 shadow-lg overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${sp.color}`} />
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <span className="text-2xl">{sp.icon}</span>
                    {sp.name}
                  </CardTitle>
                  <CardDescription>Optimal extraction parameters</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Key Compounds</p>
                    <div className="flex flex-wrap gap-2">
                      {sp.compounds.map(compound => (
                        <span key={compound} className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">
                          {compound}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Temperature</p>
                      <p className="text-lg font-bold text-gray-900">{sp.optimalTemp}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Solvent</p>
                      <p className="text-sm font-semibold text-gray-900">{sp.optimalSolvent}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Full Content */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle>Complete Extraction Database</CardTitle>
            <CardDescription>Comprehensive guide to optimal extraction methods and bioactive compound preservation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                {content}
              </pre>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
